import React, { useState } from 'react';
import PropTypes from 'prop-types';
import styled from '@emotion/styled';

import ProductHeader from '../../molecules/product-detail-header';
import ProductDescriptions from '../../molecules/product-detail-descriptions';
import ProductRestrictions from '../../molecules/product-detail-restrictions';
import ProductBadge from '../../molecules/product-badge';
import StickyProductAdd from '../../tissues/product-details-sticky-add-to-cart';
import ProductAddToCart from '../../tissues/product-details-add-to-cart';
import ProductAddToCartGift from '../../tissues/product-details-add-gift-card';
import ProductProperties from '../../tissues/product-details-properties';

const ProductInformation = styled.div`
  display: flex;
  flex-direction: column;
  grid-area: product-information;
  flex: 1;
`;

const ProductBadges = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  margin-bottom: 3rem;
  margin-top: -1rem;

  > div {
    margin: 1rem 2rem 0 0;
  }
`;

const ProductDescription = styled(ProductDescriptions)`
  @media (max-width: ${(props) => props.theme.breakpoint.medium}) {
    order: 98;
  }
`;

const ProductDetails = (props) => {
  const [activeVariant, setActiveVariant] = useState(null);
  const [activeVendor, setActiveVendor] = useState(null);
  const [monogramSelected, setMonogramSelected] = useState(false);

  const getInternationalShipping = () => {
    const id = activeVariant?.prices?.[0]?.vendor_id;
    const stockItem = activeVariant?.stock_items?.filter((item) => item.vendor_id === id);
    return !!stockItem?.[0]?.international_shipping;
  };

  const handleOnVariantChange = (variant) => {
    if (variant) {
      const maisonetteStockId = variant.stock_items.find((x) => x.stock_location_name === 'Maisonette');

      const bestMargin = variant.prices.sort((a, b) => (
        (a.price - a.offer_settings.cost_price > b.price - b.offer_settings.cost_price) ? 1 : -1
      ))[0];

      const vendor = maisonetteStockId?.vendor_id ?? bestMargin?.vendor_id;

      setActiveVariant(variant);
      setActiveVendor(vendor);
    } else {
      setActiveVariant(null);
      setActiveVendor(null);
    }
  };

  const isGiftCard = props.product.gift_card;

  const onVendorChange = (vendor) => setActiveVendor(vendor);

  const getLeadTime = () => {
    const monogram = activeVariant?.prices?.[0]?.monogram;
    let monogramLeadTime;

    if (monogram?.monogrammable_only || (monogram && monogramSelected)) {
      monogramLeadTime = monogram?.monogram_lead_time;
    }

    return monogramLeadTime ?? activeVariant?.lead_time;
  };

  return (
    <ProductInformation key={props?.product?.id}>
      {
        props.product?.trends && props.product?.trends.length > 0 && (
          <ProductBadges>
            {
              props.product.trends.map(
                (badge) => <ProductBadge key={badge.type} badge={badge.value} />
              )
            }
          </ProductBadges>
        )
      }

      <ProductHeader
        isQuickshop={props.isQuickshop}
        title={props?.product?.name}
        brand={props?.product?.brand}
        brandSlug={props?.product?.brand_slug}
        prices={props?.product?.variants?.map((v) => v.prices[0]) ?? []}
        noOffers={props?.product?.variants?.every((v) => v.price === null) ?? true}
        variant={activeVariant}
        vendor={activeVendor}
        slug={props?.product?.slug}
      />

      {
        !props.isQuickshop && (activeVariant || isGiftCard) && (
          <ProductRestrictions
            leadTime={getLeadTime()}
            finalSale={activeVariant?.prices?.[0]?.final_sale}
            importDuties={getInternationalShipping()}
            isGiftCard={isGiftCard}
          />
        )
      }

      {
        isGiftCard ? (
          <ProductAddToCartGift
            product={props.product}
            variant={activeVariant}
            vendor={activeVendor}
            onVariantChange={handleOnVariantChange}
          />
        ) : (
          <ProductAddToCart
            product={props.product}
            position={props.position}
            variant={activeVariant}
            vendor={activeVendor}
            onVariantChange={handleOnVariantChange}
            isQuickshop={props.isQuickshop}
            setMonogramSelected={setMonogramSelected}
          />
        )
      }

      { !props.isQuickshop ? (
        <StickyProductAdd
          product={props.product}
          position={props.position}
          variant={activeVariant}
          vendor={activeVendor}
          onVariantChange={handleOnVariantChange}
          setMonogramSelected={setMonogramSelected}
        />
      ) : null }

      {
        !props.isQuickshop && (
          <ProductDescription
            brand={props.product.brand}
            brandDescription={props.product.brand_description}
            description={props.product.description}
          />
        )
      }

      {
        !props.isQuickshop && (
          <ProductProperties
            order={99}
            product={props.product}
            variant={activeVariant}
            vendor={activeVendor}
            onVendorChange={onVendorChange}
          />
        )
      }

    </ProductInformation>
  );
};

ProductDetails.defaultProps = {
  isQuickshop: false,
  position: null
};

ProductDetails.propTypes = {
  product: PropTypes.object.isRequired,
  isQuickshop: PropTypes.bool,
  position: PropTypes.number
};

ProductDetails.whyDidYouRender = true;

export default ProductDetails;
